export type SkillDownloadStatus = 'NOT_DOWNLOADED' | 'UP_TO_DATE' | 'HAS_UPDATE'

export type SkillCatalogItemType = 'SKILL' | 'WORKFLOW' | 'ARTIFACT' | 'DOCUMENT' | 'INSTALLER'

/** Loại bộ nội dung: SKILL (giải nén + tự phân loại) hoặc giữ nguyên file. */
export type SkillPackContentType = 'SKILL' | 'DOCUMENT' | 'INSTALLER'

/** Tài liệu / File cài đặt: thẻ có nút tải trực tiếp. */
export function isFileCatalogType(type: SkillCatalogItemType): boolean {
  return type === 'DOCUMENT' || type === 'INSTALLER'
}

export interface SkillPack {
  id: number
  slug: string
  name: string
  description?: string | null
  contentType?: SkillPackContentType
  createdAt: string
  updatedAt: string
  fileCount?: number
  folderCount?: number
  lastDownloadedAt?: string | null
  downloadStatus?: SkillDownloadStatus
  catalogItems?: SkillCatalogItem[]
}

export interface SkillCatalogItem {
  id: number
  packId: number
  rootNodeId: number
  itemType: SkillCatalogItemType
  slug: string
  name: string
  description?: string | null
  isPublic: boolean
  isHidden: boolean
  groupPath?: string | null
  createdAt: string
  updatedAt: string
  packName?: string | null
  rootNodeType?: 'FOLDER' | 'FILE' | null
  rootFileType?: string | null
  rootSizeBytes?: number | null
}

export interface SkillTreeNode {
  id: number
  packId: number
  parentId: number | null
  nodeType: 'FOLDER' | 'FILE'
  name: string
  relativePath: string
  fileUrl?: string | null
  fileType?: string | null
  sizeBytes?: number | null
  children: SkillTreeNode[]
}

export interface CourseSkillLink {
  id: number
  courseId: number
  sessionId?: number | null
  packId: number
  nodeId: number
  title?: string | null
  position: number
  nodeName?: string
  nodeType?: string
  relativePath?: string
  packName?: string
  packSlug?: string
  /** Present on /skills/library-access aggregates */
  courseCode?: string
  courseName?: string
}

export interface TestAskAiSkillLink {
  id: number
  paperId?: number
  questionId?: number
  packId: number
  nodeId: number
  title?: string | null
  position: number
  nodeName?: string
  nodeType?: string
  relativePath?: string
  packName?: string
  packSlug?: string
}

export type SkillDownloadEventType = 'PACK' | 'FOLDER' | 'FILE'

export interface SkillDownloadEvent {
  id: number
  userId: number
  packId: number
  downloadType: SkillDownloadEventType
  nodeId?: number | null
  catalogItemId?: number | null
  courseId?: number | null
  resourceName: string
  relativePath?: string | null
  ip?: string | null
  userAgent?: string | null
  createdAt: string
  userEmail: string
  userFullName?: string | null
  packName: string
  catalogItemName?: string | null
}

export interface SkillDownloadEventsResult {
  items: SkillDownloadEvent[]
  pagination: {
    page: number
    limit: number
    total: number
    totalPages: number
  }
}

export interface SkillLibraryAccessCourse {
  id: number
  code: string
  name: string
}

export interface SkillLibraryUnlockedCatalog {
  catalogId: number
  courseId: number
  courseCode: string
}

export interface SkillLibraryAccess {
  eligible: boolean
  courses: SkillLibraryAccessCourse[]
  links: CourseSkillLink[]
  unlockedCatalog: SkillLibraryUnlockedCatalog[]
}

function authHeaders(): HeadersInit {
  const token = localStorage.getItem('accessToken')
  return token ? { Authorization: `Bearer ${token}` } : {}
}

function optionalAuthHeaders(): HeadersInit {
  return authHeaders()
}

async function parseJson(res: Response) {
  const data = await res.json().catch(() => ({}))
  if (!res.ok || data.success === false) {
    throw new Error(data.message || `HTTP ${res.status}`)
  }
  return data
}

export const SkillApi = {
  async getLibraryAccess(): Promise<SkillLibraryAccess> {
    const res = await fetch('/api/v1/skills/library-access', { headers: authHeaders() })
    const data = await parseJson(res)
    return (
      data.data || {
        eligible: false,
        courses: [],
        links: [],
        unlockedCatalog: [],
      }
    )
  },

  async listCatalog(): Promise<SkillCatalogItem[]> {
    const res = await fetch('/api/v1/skills/catalog', { headers: optionalAuthHeaders() })
    const data = await parseJson(res)
    return data.data || []
  },

  async getCatalogTree(
    catalogId: number
  ): Promise<{ item: SkillCatalogItem; tree: SkillTreeNode[]; unlockCourseId?: number | null }> {
    const res = await fetch(`/api/v1/skills/catalog/${catalogId}/tree`, {
      headers: optionalAuthHeaders(),
    })
    const data = await parseJson(res)
    return data.data
  },

  async listPacks(): Promise<SkillPack[]> {
    const res = await fetch('/api/v1/skills/packs', { headers: optionalAuthHeaders() })
    const data = await parseJson(res)
    return data.data || []
  },

  async adminListPacks(): Promise<SkillPack[]> {
    const res = await fetch('/api/v1/admin/skills/packs', { headers: authHeaders() })
    const data = await parseJson(res)
    return data.data || []
  },

  async adminListDownloadEvents(params?: {
    page?: number
    limit?: number
    email?: string
    packId?: number
    downloadType?: SkillDownloadEventType | ''
    from?: string
    to?: string
  }): Promise<SkillDownloadEventsResult> {
    const q = new URLSearchParams()
    if (params?.page) q.set('page', String(params.page))
    if (params?.limit) q.set('limit', String(params.limit))
    if (params?.email?.trim()) q.set('email', params.email.trim())
    if (params?.packId) q.set('packId', String(params.packId))
    if (params?.downloadType) q.set('downloadType', params.downloadType)
    if (params?.from) q.set('from', params.from)
    if (params?.to) q.set('to', params.to)
    const qs = q.toString()
    const res = await fetch(`/api/v1/admin/skills/download-events${qs ? `?${qs}` : ''}`, {
      headers: authHeaders(),
    })
    const data = await parseJson(res)
    return data.data
  },

  async getTree(packId: number): Promise<{ pack: SkillPack; tree: SkillTreeNode[] }> {
    const res = await fetch(`/api/v1/skills/packs/${packId}/tree`, { headers: optionalAuthHeaders() })
    const data = await parseJson(res)
    return data.data
  },

  async adminGetTree(packId: number): Promise<{ pack: SkillPack; tree: SkillTreeNode[] }> {
    const res = await fetch(`/api/v1/admin/skills/packs/${packId}/tree`, { headers: authHeaders() })
    const data = await parseJson(res)
    return data.data
  },

  async updateCatalogItem(
    id: number,
    payload: {
      name?: string
      description?: string | null
      isPublic?: boolean
      isHidden?: boolean
    }
  ): Promise<SkillCatalogItem> {
    const res = await fetch(`/api/v1/admin/skills/catalog/${id}`, {
      method: 'PUT',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
    const data = await parseJson(res)
    return data.data
  },

  async reclassifyPack(packId: number): Promise<SkillCatalogItem[]> {
    const res = await fetch(`/api/v1/admin/skills/packs/${packId}/reclassify`, {
      method: 'POST',
      headers: authHeaders(),
    })
    const data = await parseJson(res)
    return data.data || []
  },

  async createPack(payload: {
    name: string
    slug?: string
    description?: string
    contentType?: SkillPackContentType
  }): Promise<SkillPack> {
    const res = await fetch('/api/v1/admin/skills/packs', {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
    const data = await parseJson(res)
    return data.data
  },

  async updatePack(
    id: number,
    payload: { name?: string; description?: string | null }
  ): Promise<SkillPack> {
    const res = await fetch(`/api/v1/admin/skills/packs/${id}`, {
      method: 'PUT',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
    const data = await parseJson(res)
    return data.data
  },

  async deletePack(id: number): Promise<void> {
    const res = await fetch(`/api/v1/admin/skills/packs/${id}`, {
      method: 'DELETE',
      headers: authHeaders(),
    })
    await parseJson(res)
  },

  async uploadNew(formData: FormData): Promise<{ pack: SkillPack; fileCount: number }> {
    const res = await fetch('/api/v1/admin/skills/packs/upload-new', {
      method: 'POST',
      headers: authHeaders(),
      body: formData,
    })
    const data = await parseJson(res)
    return data.data
  },

  async uploadReplace(packId: number, formData: FormData): Promise<{ pack: SkillPack; fileCount: number }> {
    const res = await fetch(`/api/v1/admin/skills/packs/${packId}/upload`, {
      method: 'POST',
      headers: authHeaders(),
      body: formData,
    })
    const data = await parseJson(res)
    return data.data
  },

  async deleteNode(nodeId: number): Promise<void> {
    const res = await fetch(`/api/v1/admin/skills/nodes/${nodeId}`, {
      method: 'DELETE',
      headers: authHeaders(),
    })
    await parseJson(res)
  },

  async uploadIntoFolder(folderNodeId: number, formData: FormData): Promise<{ pack: SkillPack; fileCount: number }> {
    const res = await fetch(`/api/v1/admin/skills/folders/${folderNodeId}/upload`, {
      method: 'POST',
      headers: authHeaders(),
      body: formData,
    })
    const data = await parseJson(res)
    return data.data
  },

  async getFileContent(
    nodeId: number,
    opts?: { courseId?: number }
  ): Promise<{
    id: number
    name: string
    fileType: string
    relativePath: string
    content: string
  }> {
    const q = opts?.courseId ? `?courseId=${opts.courseId}` : ''
    const res = await fetch(`/api/v1/skills/files/${nodeId}/content${q}`, {
      headers: optionalAuthHeaders(),
    })
    const data = await parseJson(res)
    return data.data
  },

  /** Resolve a path written inside a file (vd ảnh cùng thư mục) into a file node. */
  async resolveRelativeFile(
    nodeId: number,
    relativePath: string,
    opts?: { courseId?: number }
  ): Promise<{ id: number; name: string; fileType: string; relativePath: string }> {
    const params = new URLSearchParams({ path: relativePath })
    if (opts?.courseId) params.set('courseId', String(opts.courseId))
    const res = await fetch(`/api/v1/skills/files/${nodeId}/resolve?${params.toString()}`, {
      headers: optionalAuthHeaders(),
    })
    const data = await parseJson(res)
    return data.data
  },

  fileStreamUrl(nodeId: number, opts?: { courseId?: number; raw?: boolean }): string {
    const params = new URLSearchParams()
    if (opts?.courseId) params.set('courseId', String(opts.courseId))
    if (opts?.raw) params.set('raw', '1')
    const q = params.toString()
    return `/api/v1/skills/files/${nodeId}/content${q ? `?${q}` : ''}`
  },

  async downloadPack(packId: number, filename: string): Promise<void> {
    const res = await fetch(`/api/v1/skills/packs/${packId}/download`, { headers: authHeaders() })
    if (!res.ok) {
      const data = await res.json().catch(() => ({}))
      throw new Error(data.message || 'Không tải được bộ skill')
    }
    const blob = await res.blob()
    triggerBlobDownload(blob, filename.endsWith('.zip') ? filename : `${filename}.zip`)
  },

  async downloadFolder(
    nodeId: number,
    folderName: string,
    opts?: { courseId?: number }
  ): Promise<void> {
    const q = opts?.courseId ? `?courseId=${opts.courseId}` : ''
    const res = await fetch(`/api/v1/skills/folders/${nodeId}/download${q}`, {
      headers: authHeaders(),
    })
    if (!res.ok) {
      const data = await res.json().catch(() => ({}))
      throw new Error(data.message || 'Không tải được thư mục')
    }
    const blob = await res.blob()
    // Ưu tiên tên từ server (basename thư mục skill), tránh dùng tên hiển thị / thư mục cha.
    const fromHeader = filenameFromContentDisposition(res.headers.get('Content-Disposition'))
    const fallback = folderName.endsWith('.zip') ? folderName : `${folderName}.zip`
    triggerBlobDownload(blob, fromHeader || fallback)
  },

  async downloadFile(nodeId: number, fileName: string, opts?: { courseId?: number }): Promise<void> {
    const url = this.fileStreamUrl(nodeId, { courseId: opts?.courseId, raw: true })
    const res = await fetch(url, { headers: authHeaders() })
    if (!res.ok) {
      const data = await res.json().catch(() => ({}))
      throw new Error(data.message || 'Không tải được file')
    }
    const blob = await res.blob()
    // Ưu tiên tên file gốc từ server (giữ đuôi .zip/.pdf... kể cả khi tên hiển thị đã đổi).
    const fromHeader = filenameFromContentDisposition(res.headers.get('Content-Disposition'))
    triggerBlobDownload(blob, fromHeader || fileName)
  },

  async getLinkedTree(
    nodeId: number,
    courseId: number
  ): Promise<{ node: any; pack: any; tree: SkillTreeNode[] }> {
    const res = await fetch(`/api/v1/skills/links/${nodeId}/tree?courseId=${courseId}`, {
      headers: authHeaders(),
    })
    const data = await parseJson(res)
    return data.data
  },

  async listCourseSkillLinks(courseId: number, asAdmin = false): Promise<CourseSkillLink[]> {
    const url = asAdmin
      ? `/api/v1/admin/courses/${courseId}/skill-links`
      : `/api/v1/student/courses/${courseId}/skill-links`
    const res = await fetch(url, { headers: authHeaders() })
    const data = await parseJson(res)
    return data.data || []
  },

  async addCourseSkillLinks(
    courseId: number,
    payload: { nodeIds?: number[]; parentNodeId?: number; expandChildren?: boolean; sessionId?: number | null }
  ): Promise<CourseSkillLink[]> {
    const res = await fetch(`/api/v1/admin/courses/${courseId}/skill-links`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
    const data = await parseJson(res)
    return data.data || []
  },

  async deleteCourseSkillLink(courseId: number, linkId: number): Promise<void> {
    const res = await fetch(`/api/v1/admin/courses/${courseId}/skill-links/${linkId}`, {
      method: 'DELETE',
      headers: authHeaders(),
    })
    await parseJson(res)
  },

  async bulkDeleteCourseSkillLinks(
    courseId: number,
    payload: { linkIds?: number[]; all?: boolean }
  ): Promise<number> {
    const res = await fetch(`/api/v1/admin/courses/${courseId}/skill-links/bulk-delete`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
    const data = await parseJson(res)
    return data.data?.deleted ?? 0
  },

  async listPacksPicker(): Promise<(SkillPack & { tree: SkillTreeNode[] })[]> {
    const res = await fetch('/api/v1/admin/skill-packs-picker', { headers: authHeaders() })
    const data = await parseJson(res)
    return data.data || []
  },

  async listTestAskAiSkillLinks(
    scope: 'paper' | 'question',
    ownerId: number
  ): Promise<TestAskAiSkillLink[]> {
    const path =
      scope === 'paper'
        ? `/api/v1/admin/tests/${ownerId}/skill-links`
        : `/api/v1/admin/questions/${ownerId}/skill-links`
    const res = await fetch(path, { headers: authHeaders() })
    const data = await parseJson(res)
    return data.data || []
  },

  async addTestAskAiSkillLinks(
    scope: 'paper' | 'question',
    ownerId: number,
    payload: { nodeIds?: number[]; parentNodeId?: number; expandChildren?: boolean }
  ): Promise<TestAskAiSkillLink[]> {
    const path =
      scope === 'paper'
        ? `/api/v1/admin/tests/${ownerId}/skill-links`
        : `/api/v1/admin/questions/${ownerId}/skill-links`
    const res = await fetch(path, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
    const data = await parseJson(res)
    return data.data || []
  },

  async bulkDeleteTestAskAiSkillLinks(
    scope: 'paper' | 'question',
    ownerId: number,
    payload: { linkIds?: number[]; all?: boolean }
  ): Promise<number> {
    const path =
      scope === 'paper'
        ? `/api/v1/admin/tests/${ownerId}/skill-links/bulk-delete`
        : `/api/v1/admin/questions/${ownerId}/skill-links/bulk-delete`
    const res = await fetch(path, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
    const data = await parseJson(res)
    return data.data?.deleted ?? 0
  },
}

function triggerBlobDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

/** Parse filename from Content-Disposition (supports filename*=UTF-8''...). */
function filenameFromContentDisposition(header: string | null): string | null {
  if (!header) return null
  const star = header.match(/filename\*\s*=\s*(?:UTF-8''|utf-8'')([^;]+)/i)
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1].trim().replace(/^["']|["']$/g, ''))
    } catch {
      /* keep falling through */
    }
  }
  const plain = header.match(/filename\s*=\s*("([^"]+)"|([^;]+))/i)
  const raw = (plain?.[2] || plain?.[3] || '').trim()
  return raw || null
}

/** Basename của relativePath — tên thư mục skill thật sự trên disk. */
export function skillFolderBasename(relativePath?: string | null, fallback?: string): string {
  const parts = String(relativePath || '')
    .replace(/\\/g, '/')
    .split('/')
    .filter(Boolean)
  return parts[parts.length - 1] || fallback || 'skill'
}

export function formatSkillDate(value?: string | null): string {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString('vi-VN')
}

export function downloadStatusLabel(status?: SkillDownloadStatus): string {
  switch (status) {
    case 'UP_TO_DATE':
      return 'Đã lấy bản mới nhất'
    case 'HAS_UPDATE':
      return 'Có bản mới'
    default:
      return 'Chưa tải'
  }
}

export function catalogTypeLabel(type: SkillCatalogItemType): string {
  switch (type) {
    case 'SKILL':
      return 'Skill'
    case 'WORKFLOW':
      return 'Workflow'
    case 'ARTIFACT':
      return 'Artifact'
    case 'DOCUMENT':
      return 'Tài liệu'
    case 'INSTALLER':
      return 'File cài đặt'
    default:
      return type
  }
}

export function packContentTypeLabel(type?: SkillPackContentType | null): string {
  switch (type) {
    case 'DOCUMENT':
      return 'Tài liệu'
    case 'INSTALLER':
      return 'File cài đặt'
    default:
      return 'Skill'
  }
}

export function formatFileSize(bytes?: number | null): string {
  if (bytes == null || Number.isNaN(bytes)) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

/** Human-readable group title from groupPath (thư mục gom khi upload). */
export function formatGroupTitle(
  groupPath: string | null | undefined,
  itemType?: SkillCatalogItemType,
  packName?: string | null
): string {
  if (itemType === 'DOCUMENT') return packName ? `Tài liệu · ${packName}` : 'Tài liệu'
  if (itemType === 'INSTALLER') return packName ? `File cài đặt · ${packName}` : 'File cài đặt'
  if (itemType === 'WORKFLOW' && (!groupPath || groupPath === 'workflow' || groupPath === 'workflows')) {
    return 'Workflow'
  }
  if (itemType === 'ARTIFACT' && !groupPath) {
    return 'Artifact / file lẻ'
  }
  if (!groupPath) return 'Khác'

  let path = groupPath.replace(/\\/g, '/')
  // Bỏ prefix skills/ để gọn: skills/nhom-A → nhom-A
  const skillsIdx = path.toLowerCase().lastIndexOf('skills/')
  if (skillsIdx >= 0) {
    const after = path.slice(skillsIdx + 'skills/'.length)
    if (after) return after.split('/').join(' · ')
    return 'Skills'
  }
  if (path.toLowerCase() === 'skills') return 'Skills (nằm trực tiếp)'
  if (path.toLowerCase() === 'workflow' || path.toLowerCase() === 'workflows') return 'Workflow'
  return path.split('/').join(' · ')
}

export function groupCatalogKey(item: SkillCatalogItem): string {
  if (item.itemType === 'WORKFLOW') return `wf:${item.groupPath || 'workflow'}`
  if (item.itemType === 'ARTIFACT') return `art:${item.groupPath || '_root'}`
  if (item.itemType === 'DOCUMENT') return `doc:${item.packId}`
  if (item.itemType === 'INSTALLER') return `ins:${item.packId}`
  return `sk:${item.groupPath || '_other'}`
}
